import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { endpoints, formatApiError } from "../lib/api";
import { kvSet, kvGet } from "../lib/db";
import { pullFromServer } from "../lib/sync";

const AuthCtx = createContext(null);

const TOKEN_LS_KEY = "rom_access_token";
const TOKEN_KV_KEY = "rom_access_token";
const REAL_ONLINE_PING_MS = 25000; // background reachability probe interval

/* Token is mirrored into both localStorage (synchronous, fast) and IndexedDB
 * (more durable on installed PWAs — iOS/Android can evict localStorage). */
async function persistToken(token) {
    if (!token) return;
    try { localStorage.setItem(TOKEN_LS_KEY, token); } catch {}
    try { await kvSet(TOKEN_KV_KEY, token); } catch {}
}
async function clearToken() {
    try { localStorage.removeItem(TOKEN_LS_KEY); } catch {}
    try { await kvSet(TOKEN_KV_KEY, null); } catch {}
}
export async function getStoredToken() {
    let t = null;
    try { t = localStorage.getItem(TOKEN_LS_KEY); } catch {}
    if (t) return t;
    try { t = await kvGet(TOKEN_KV_KEY); } catch {}
    if (t) {
        // Re-hydrate localStorage so the axios interceptor (sync) picks it up.
        try { localStorage.setItem(TOKEN_LS_KEY, t); } catch {}
    }
    return t || null;
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = logged out, object = logged in
    // `online` reflects REAL server reachability, not just the device's
    // network interface — navigator.onLine reads true on wifi-with-no-internet
    // or weak cell signal, which would otherwise falsely enable online-only
    // features (e.g. the AI Estimator). navigator.onLine is used only as a
    // first-pass filter (skip the ping entirely when the interface is down).
    const [online, setOnline] = useState(navigator.onLine);
    const userRef = useRef(user);
    useEffect(() => { userRef.current = user; }, [user]);

    /* Verifies real connectivity via a lightweight, unauthenticated backend
     * ping (GET /api/health). Returns the resolved boolean and updates state. */
    const checkRealConnectivity = useCallback(async () => {
        if (!navigator.onLine) {
            setOnline(false);
            return false;
        }
        try {
            await endpoints.health();
            setOnline(true);
            return true;
        } catch {
            setOnline(false);
            return false;
        }
    }, []);

    useEffect(() => {
        const onUp = async () => {
            // The browser's interface just came up — verify it's actually
            // reachable before trusting it, then re-warm the cache.
            const reachable = await checkRealConnectivity();
            if (reachable && userRef.current) pullFromServer().catch(() => {});
        };
        // The interface going down is a reliable, immediate offline signal.
        const onDown = () => setOnline(false);
        window.addEventListener("online", onUp);
        window.addEventListener("offline", onDown);

        // Background probe: catches "online per the OS, but no real internet"
        // (e.g. wifi captive portal, weak cell signal) which never fires a
        // browser online/offline event since the interface itself stays up.
        checkRealConnectivity();
        const interval = setInterval(checkRealConnectivity, REAL_ONLINE_PING_MS);

        return () => {
            window.removeEventListener("online", onUp);
            window.removeEventListener("offline", onDown);
            clearInterval(interval);
        };
    }, [checkRealConnectivity]);

    useEffect(() => {
        (async () => {
            // Re-hydrate token from IndexedDB into localStorage first (PWA durability fix).
            await getStoredToken();
            const cached = await kvGet("current_user");

            // If we already have a cached user, show the app immediately and
            // re-validate against the server in the background. This avoids the
            // "blank → login screen" flash on offline PWA cold-starts.
            if (cached) {
                setUser(cached);
                try {
                    const { data } = await endpoints.me();
                    setUser(data);
                    await kvSet("current_user", data);
                } catch {
                    // Stay on cached user when offline / server unreachable.
                }
                return;
            }

            // No cached user — must hit the server.
            try {
                const { data } = await endpoints.me();
                setUser(data);
                await kvSet("current_user", data);
            } catch {
                setUser(false);
            }
        })();
    }, []);

    const login = useCallback(async (email, password) => {
        try {
            const { data } = await endpoints.login({ email, password });
            if (data.access_token) await persistToken(data.access_token);
            setUser(data.user);
            await kvSet("current_user", data.user);
            return { ok: true, user: data.user };
        } catch (e) {
            const isNetwork = !e?.response; // axios sets `response` only on HTTP errors.
            const msg = isNetwork
                ? "Can't reach the server — you appear to be offline. First sign-in must happen online; once signed in here, the app works offline."
                : formatApiError(e);
            return { ok: false, error: msg, offline: isNetwork };
        }
    }, []);

    const logout = useCallback(async () => {
        try { await endpoints.logout(); } catch {}
        await clearToken();
        await kvSet("current_user", null);
        setUser(false);
    }, []);

    return (
        <AuthCtx.Provider value={{ user, online, login, logout, setUser }}>
            {children}
        </AuthCtx.Provider>
    );
}

export const useAuth = () => useContext(AuthCtx);
